import type { LoanDocument } from "@/types";

export type KeyFactsStatementTitleKey =
  | "documents.key_facts_statement_title"
  | "documents.ksa_bnpl_summary_of_terms_title";

/** KSA market (ISO-3166 alpha-2 or alpha-3). */
export function isKsaMarket(
  countryCode: string | null | undefined,
): boolean {
  const cc = (countryCode ?? "").toUpperCase();
  return cc === "SA" || cc === "SAU";
}

/** KFS link/modal title: KSA uses BNPL Summary of Terms; UAE and others use Key facts statement. */
export function getKeyFactsStatementTitleKey(
  countryCode: string | null | undefined,
): KeyFactsStatementTitleKey {
  return isKsaMarket(countryCode)
    ? "documents.ksa_bnpl_summary_of_terms_title"
    : "documents.key_facts_statement_title";
}

export function isKeyFactsStatementType(documentType: string): boolean {
  const t = documentType.toUpperCase();
  return (
    t.includes("KEY_FACT") ||
    t.includes("KEY_FACTS") ||
    t.includes("KEY_FACT_STATEMENT") ||
    t.includes("KFS")
  );
}

/**
 * Splits session documents into Key Facts Statement vs other (e.g. Murabaha) finance documents.
 */
export function partitionCheckoutDocuments(
  documents: LoanDocument[] | null | undefined,
): {
  keyFactsDocuments: LoanDocument[];
  murabahaDocuments: LoanDocument[];
} {
  const list = documents ?? [];
  const keyFactsDocuments: LoanDocument[] = [];
  const murabahaDocuments: LoanDocument[] = [];
  for (const doc of list) {
    if (isKeyFactsStatementType(doc.documentItemType ?? "")) {
      keyFactsDocuments.push(doc);
    } else {
      murabahaDocuments.push(doc);
    }
  }
  return { keyFactsDocuments, murabahaDocuments };
}

/**
 * Label for a document row in a list. KFS types reuse the global KFS title (no per-type i18n keys).
 */
export function getDocumentListItemLabel(
  documentItemType: string,
  t: (key: string) => string,
  countryCode?: string | null,
): string {
  if (isKeyFactsStatementType(documentItemType)) {
    return t(getKeyFactsStatementTitleKey(countryCode));
  }
  return t(`document.${documentItemType}.name`);
}

/**
 * Picks the Key Facts Statement document for display (locale-aware when EN/AR variants exist).
 */
function keyFactsDocumentIsRenderable(document: LoanDocument | null): boolean {
  if (!document) return false;
  return (
    Boolean((document.id ?? "").trim()) ||
    Boolean((document.url ?? "").trim())
  );
}

/**
 * KFS acknowledgement is driven only by session documents (e.g. loan_documents from BE).
 * Independent of needs_document_signing, product type, tenure, or country.
 */
export function shouldShowKfsAcknowledgementLine(
  documents: LoanDocument[] | undefined | null,
  language: string,
): boolean {
  return keyFactsDocumentIsRenderable(
    selectKeyFactsStatementDocument(documents, language),
  );
}

export function selectKeyFactsStatementDocument(
  documents: LoanDocument[] | undefined | null,
  language: string,
): LoanDocument | null {
  const list = documents ?? [];
  const kfsDocs = list.filter((doc) =>
    isKeyFactsStatementType(doc.documentItemType ?? ""),
  );
  if (kfsDocs.length === 0) return null;

  const isArabic = language.startsWith("ar");

  const findBySubtype = (needle: string) =>
    kfsDocs.find((doc) =>
      (doc.documentItemType ?? "").toUpperCase().includes(needle),
    );

  if (isArabic) {
    return (
      findBySubtype("KEY_FACTS_STATEMENT_AR") ??
      findBySubtype("_AR") ??
      kfsDocs.find((doc) =>
        /(^|_)AR($|_)/i.test(doc.documentItemType ?? ""),
      ) ??
      kfsDocs[0]
    );
  }

  return (
    findBySubtype("KEY_FACTS_STATEMENT_EN") ??
    findBySubtype("_EN") ??
    kfsDocs.find((doc) =>
      /(^|_)EN($|_)/i.test(doc.documentItemType ?? ""),
    ) ??
    kfsDocs[0]
  );
}
